import React, { useState, useEffect, useMemo } from 'react';
import { ReactSortable as Sortable } from 'react-sortablejs';
import InfoBox from 'src/components/atoms/InfoBox';
import styled from 'styled-components';
import { Typography, Collapse, Divider, Box, CircularProgress, Tooltip } from '@material-ui/core';
import useOnlineStatus from 'src/hooks/useOnlineStatus';
import TugLinePositions from './TugLinePositions';
import { addTugStage, updateTugStage, updateTugStages } from 'src/utils/tugStagesApi';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Modal from 'src/components/molecules/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { pilotageSelector, isPilotageDoneSelector } from 'src/store/pilotage/selectors';
import SubHeading from 'src/components/atoms/SubHeading';
import PrimaryButton from 'src/components/atoms/PrimaryButton';
import { addSuccessToast } from 'src/store/toast/actions';
import { pilotageTugStagesRequest, pilotageTugStagesDeleteRequest } from 'src/store/pilotageTugStages/actions';
import { pilotageTugsSelector } from 'src/store/pilotageTugs/selectors';
import DragHandleIcon from '@material-ui/icons/DragHandle';

const AccordionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: ${({ theme }) => theme.spacing(1)}px;
    padding: ${({ theme }) => theme.spacing(2)}px ${({ theme }) => theme.spacing(3)}px;
    cursor: pointer;
    min-height: 40px;
    margin-bottom: 0;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 25px;
`;

const IconButton = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    background: transparent;
    transition: background 0.2s;
    &:hover {
        background: ${({ theme }) => theme.palette.action.hover};
    }
`;

interface CollapseButtonProps {
    open: boolean;
}
const CollapseButton = styled(ExpandMoreIcon) <CollapseButtonProps>`
    && {
        border: 1px solid;
        border-radius: 100%;
        transform: ${({ open }) => `rotate(${open ? 180 : 0}deg)`};
        transition: transform 400ms;
    }
`;
const PrimaryButtonStyle = styled(PrimaryButton)`height: 50%;`

type TabState = {
    uuid: string;
    sequence: number;
    label: string;
    isFallback?: boolean;
};

const mapStagesToTabs = (stages: any): TabState[] => {
    if (!stages) return [];
    const stageArray = Array.isArray(stages) ? stages : Object.values(stages);
    return (stageArray || [])
        .map((stage: any) => ({
            uuid: stage && stage.uuid ? stage.uuid : DEFAULT_STAGE_UUID,
            sequence: stage && typeof stage.sequence === 'number' ? stage.sequence : 1,
            label: stage && stage.name ? stage.name : DEFAULT_STAGE_LABEL,
            isFallback: !(stage && stage.uuid),
        }))
        .sort((a: any, b: any) => (a.sequence || 0) - (b.sequence || 0));
};

const DEFAULT_STAGE_UUID = '__default_stage__';
const DEFAULT_STAGE_LABEL = 'Stage 1';

const dedupeTugsByLine = (source: any[]): any[] => {
    if (!Array.isArray(source)) {
        return [];
    }
    const seenLines = new Set<string>();
    const ordered: any[] = [];
    source.forEach(item => {
        if (!item) {
            return;
        }
        const lineNumber = item.lineNumber;
        if (lineNumber === undefined || lineNumber === null) {
            return;
        }
        const key = String(lineNumber);
        if (seenLines.has(key)) {
            return;
        }
        seenLines.add(key);
        ordered.push(item);
    });
    return ordered;
};

const TugLinePositionsTabs = () => {
    const [tabs, setTabs] = useState<TabState[]>([]);
    const [openIdx, setOpenIdx] = useState<number[]>([0]); // First expanded by default, allow multiple
    const [loading, setLoading] = useState(false);
    const isOnline = useOnlineStatus();
    const [showStageDialog, setShowStageDialog] = useState(false);
    const [stageName, setStageName] = useState('');
    const [stageError, setStageError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedStage, setSelectedStage] = useState<TabState | null>(null);
    const [showReorderDialog, setShowReorderDialog] = useState(false);
    const [reorderTabs, setReorderTabs] = useState<TabState[]>([]);
    const [reorderSaving, setReorderSaving] = useState(false);
    const isPilotageDone = useSelector(isPilotageDoneSelector)
    const dispatch = useDispatch()
    const [editStageName, setEditStageName] = useState('');
    const pilotage = useSelector(pilotageSelector)
    const pilotageUuid = pilotage && pilotage.data.uuid
    const pilotageTugsState = useSelector(pilotageTugsSelector);
    const pilotageTugsObj = pilotageTugsState && pilotageTugsState.data ? pilotageTugsState.data : {};
    const pilotageTugsList = useMemo(() => (pilotageTugsState && Array.isArray(pilotageTugsState.list) ? pilotageTugsState.list : []), [pilotageTugsState]);
    const fallbackStageTugs = useMemo(() => {
        if (!pilotageTugsList.length) {
            return [];
        }
        const stageLessTugs = pilotageTugsList.filter((item: any) => item && (!item.stage || !item.stage.uuid));
        return dedupeTugsByLine(stageLessTugs);
    }, [pilotageTugsList]);

    const pilotageTugStagesState = useSelector((state: any) => state.pilotageTugStages || {});
    const pilotageTugStagesData = pilotageTugStagesState.data || {};
    const isTugUpdateInProgress = Boolean(pilotageTugsState && pilotageTugsState.isLoading);
    const vesselState = useSelector((state: any) => state.vessel || {});
    const isSwlUpdateInProgress = Boolean(vesselState && vesselState.isLoading);
    const stageManagementDisabled = fallbackStageTugs.length > 0;
    const isStageMutationDisabled = stageManagementDisabled || loading || reorderSaving || isTugUpdateInProgress || isSwlUpdateInProgress;
    const isStageActionsLocked = isStageMutationDisabled || !isOnline || isPilotageDone;
    const stageActionDisableMessage = () => {
        if (stageManagementDisabled) return "Stage management is currently disabled for legacy tug data.";
        if (!isOnline) return "This action is not available while offline";
        if (isPilotageDone) return "This action is not available";
        if (isTugUpdateInProgress || isSwlUpdateInProgress) return "This action is temporarily disabled while tug or SWL updates are in progress";
        return "";
    };
    const nonFallbackTabs = useMemo(() => tabs.filter(tab => !tab.isFallback), [tabs]);
    const tugsByStage: Record<string, any[]> = useMemo(() => {
        const result: Record<string, any[]> = {};
        tabs.forEach(tab => {
            if (tab.uuid === DEFAULT_STAGE_UUID) {
                result[tab.uuid] = fallbackStageTugs;
            } else {
                const stageEntry = pilotageTugsObj && pilotageTugsObj[tab.uuid];
                const stageTugs = stageEntry ? Object.values(stageEntry) : [];
                result[tab.uuid] = dedupeTugsByLine(stageTugs as any[]);
            }
        });
        return result;
    }, [tabs, fallbackStageTugs, pilotageTugsObj]);

    useEffect(() => {
        if (!pilotageUuid) {
            return;
        }

        (async () => {
            setLoading(true);
            const result = await dispatch(pilotageTugStagesRequest(pilotageUuid));
            if (!result) {
                setStageError('Failed loading stages');
                setTabs([]);
            } else {
                setStageError('');
            }
            setLoading(false);
        })();
    }, [dispatch, pilotageUuid]);

    useEffect(() => {
        const mappedTabs = mapStagesToTabs(pilotageTugStagesData);
        const fallbackNeeded = fallbackStageTugs.length > 0;
        const hasFallbackTab = mappedTabs.some(tab => tab.uuid === DEFAULT_STAGE_UUID);

        if (fallbackNeeded && !hasFallbackTab) {
            const fallbackTab: TabState = {
                uuid: DEFAULT_STAGE_UUID,
                sequence: 1,
                label: DEFAULT_STAGE_LABEL,
                isFallback: true,
            };
            const nextTabs = [fallbackTab, ...mappedTabs];
            setTabs(nextTabs);
        } else if (!fallbackNeeded) {
            setTabs(mappedTabs);
        } else {
            setTabs(mappedTabs);
        }
    }, [pilotageTugStagesData, fallbackStageTugs]);


    const openStageDialog = () => {
        setStageName('');
        setStageError('');
        setShowStageDialog(true);
    };

    const openReorderDialog = () => {
        setReorderTabs(tabs.filter(tab => !tab.isFallback).map(t => ({ ...t })));
        setShowReorderDialog(true);
    };

    const closeReorderDialog = () => {
        setShowReorderDialog(false);
        setReorderTabs([]);
    };

    const handleReorderSave = async () => {
        if (isStageMutationDisabled) {
            return;
        }
        setReorderSaving(true);
        setLoading(true);
        try {
            const updated = reorderTabs.map((tab, idx) => ({ ...tab, sequence: idx + 1 }));
            const payload = updated.map(tab => ({ uuid: tab.uuid, sequence: tab.sequence }));
            await updateTugStages(payload);
            await dispatch(pilotageTugStagesRequest(pilotageUuid))
            setTabs(prev => {
                const fallbackTabs = prev.filter(tab => tab.isFallback);
                const next = [...fallbackTabs, ...updated].sort(
                    (a, b) => (a.sequence || 0) - (b.sequence || 0)
                );
                return next;
            });
            await dispatch(addSuccessToast({ message: 'Stages reordered' }));
            closeReorderDialog();
        } catch (err) {
            setStageError('Failed to save new stage order');
        } finally {
            setReorderSaving(false);
            setLoading(false);
        }
    };

    const closeStageDialog = () => {
        setShowStageDialog(false);
        setStageName('');
        setStageError('');
    };

    const handleStageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStageName(e.target.value);
        setStageError('');
    };

    const handleStageDialogSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isStageMutationDisabled) {
            return;
        }
        if (!stageName.trim()) {
            setStageError('Stage name is required');
            return;
        }
        const duplicate = nonFallbackTabs.find(tab => tab.label.toLowerCase() === stageName.trim().toLowerCase());
        if (duplicate) {
            setStageError('Stage name already exists');
            return;
        }

        setLoading(true);
        try {
            const highestSequence = nonFallbackTabs.reduce((acc, tab) => {
                const seq = typeof tab.sequence === 'number' ? tab.sequence : 0;
                return Math.max(acc, seq);
            }, 0);
            const fallbackStageExists = tabs.some(tab => tab.isFallback);
            const defaultSequence = fallbackStageExists ? 2 : 1;
            const nextSequence = highestSequence > 0 ? highestSequence + 1 : defaultSequence;

            await addTugStage({
                name: stageName.trim(),
                sequence: nextSequence,
                pilotage: {
                    uuid: pilotageUuid
                }
            });
            await dispatch(pilotageTugStagesRequest(pilotageUuid))
            dispatch(addSuccessToast({ message: 'Stage created successfully' }));
            closeStageDialog();
        } catch (error) {
            setStageError('Failed to create stage');
        } finally {
            setLoading(false);
        }
    };

    const closeDeleteStageDialog = () => {
        setShowDeleteModal(false);
        setSelectedStage(null);
        setStageError('');
    }

    const closeEditStageDialog = () => {
        setShowEditModal(false);
        setSelectedStage(null);
        setStageError('');
    };

    const handleDeleteStage = async () => {
        if (!selectedStage || selectedStage.isFallback || isStageMutationDisabled) return;
        setLoading(true);
        try {
            const success = await dispatch(pilotageTugStagesDeleteRequest(pilotageUuid, selectedStage.uuid));
            if (!success) {
                setStageError('Failed to delete stage');
                return;
            }
            setShowDeleteModal(false);
            setSelectedStage(null);
            setOpenIdx([0]);
            dispatch(addSuccessToast({ message: 'Stage deleted successfully' }));
        } catch (error) {
            setStageError('Failed to delete stage');
        } finally {
            setLoading(false);
        }
    };

    const handleEditStage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isStageMutationDisabled) {
            return;
        }
        if (!selectedStage || selectedStage.isFallback) return;
        if (!editStageName.trim()) {
            setStageError('Stage name is required');
            return;
        }
        const duplicate = nonFallbackTabs.find(tab => tab.label.toLowerCase() === editStageName.trim().toLowerCase() && tab.uuid !== selectedStage.uuid);
        if (duplicate) {
            setStageError('Stage name already exists');
            return;
        }

        setLoading(true);
        try {
            await updateTugStage(selectedStage.uuid, { name: editStageName.trim() });
            await dispatch(pilotageTugStagesRequest(pilotageUuid))
            setShowEditModal(false);
            setSelectedStage(null);
            dispatch(addSuccessToast({ message: 'Stage updated successfully' }));
        } catch (error) {
            setStageError('Failed to rename stage');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Box display="flex" justifyContent="space-between">
                <Box mb={5}>
                    <Typography variant="h1">Tug Details</Typography>
                    <SubHeading>Select tug(s) and line(s) positions</SubHeading>
                    {stageManagementDisabled && <SubHeading style={{ color: 'red' }}>Editing tugs is disabled for this pilotage</SubHeading>}
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <PrimaryButtonStyle
                        onClick={openStageDialog}
                        disabled={stageManagementDisabled || isStageActionsLocked}
                        title={stageManagementDisabled ? "Stage management is currently disabled for legacy tug data." : (stageActionDisableMessage() || undefined)}
                    >
                        Add Stage
                    </PrimaryButtonStyle>
                    <PrimaryButtonStyle
                        onClick={openReorderDialog}
                        disabled={stageManagementDisabled || isStageActionsLocked || nonFallbackTabs.length <= 1}
                        title={stageManagementDisabled ? "Stage management is currently disabled for legacy tug data." : (stageActionDisableMessage() || undefined)}
                    >
                        Reorder Stages
                    </PrimaryButtonStyle>
                </Box>
            </Box>
            {
                loading ? (<div style={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={32} />
                </div>) : (
                    <div style={{ marginBottom: 32 }}>
                        {tabs.map((tab, idx) => {
                            const expanded = openIdx.includes(idx);
                            const isFallbackStage = Boolean(tab.isFallback);
                            const tabActionsDisabled = isStageActionsLocked || isFallbackStage;
                            const fallbackHint = 'This stage is managed automatically for legacy tugs';
                            const renameTooltipTitle = tabActionsDisabled
                                ? (isFallbackStage ? fallbackHint : stageActionDisableMessage())
                                : 'Rename Stage';
                            const deleteTooltipTitle = tabActionsDisabled
                                ? (isFallbackStage ? fallbackHint : stageActionDisableMessage())
                                : 'Delete Stage';
                            return (
                                <div key={tab.uuid} style={{ marginBottom: 16 }}>
                                    {tabs.length > 1 && (<AccordionHeader onClick={() => {
                                        setOpenIdx(prev =>
                                            prev.includes(idx)
                                                ? prev.filter(i => i !== idx)
                                                : [...prev, idx]
                                        );
                                    }}>
                                        <Typography variant="h2">{tab.label}</Typography>
                                        <HeaderActions>
                                            <Tooltip title={renameTooltipTitle || "Rename Stage"}>
                                                <span>
                                                    <IconButton
                                                        style={{
                                                            padding: 1,
                                                            opacity: tabActionsDisabled ? 0.5 : 1,
                                                            cursor: tabActionsDisabled ? 'not-allowed' : 'pointer',
                                                        }}
                                                        onClick={e => {
                                                            if (tabActionsDisabled) return;
                                                            e.stopPropagation();
                                                            setSelectedStage(tab);
                                                            setEditStageName(tab.label);
                                                            setShowEditModal(true);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="large" style={{ color: '#1976d2' }} />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={deleteTooltipTitle || "Delete Stage"} >
                                                <span>
                                                    <IconButton
                                                        style={{
                                                            padding: 1,
                                                            opacity: tabActionsDisabled ? 0.5 : 1,
                                                            cursor: tabActionsDisabled ? 'not-allowed' : 'pointer'
                                                        }}
                                                        onClick={e => {
                                                            if (tabActionsDisabled) return;
                                                            e.stopPropagation();
                                                            setSelectedStage(tab);
                                                            setShowDeleteModal(true);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="large" style={{ color: '#d32f2f' }} />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <CollapseButton open={expanded} fontSize="large" />
                                        </HeaderActions>
                                    </AccordionHeader>)}
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        <InfoBox mt={5} variant={undefined}>
                                            {(
                                                <TugLinePositions
                                                    key={tab.sequence}
                                                    stageUuid={tab.uuid}
                                                    pilotageTugs={tugsByStage[tab.uuid] || []}
                                                    readOnly={isPilotageDone || isFallbackStage}
                                                />
                                            ) as any}
                                        </InfoBox>
                                    </Collapse>
                                    <Divider />
                                </div>
                            );
                        })}
                        {/* Reorder Dialog */}
                        <Modal
                            open={showReorderDialog}
                            onClose={closeReorderDialog as any}
                            heading="Reorder Stages"
                        >
                            <div style={{ minWidth: 360 }}>
                                <Sortable
                                    tag="div"
                                    disabled={isStageMutationDisabled}
                                    list={reorderTabs.map(t => ({ ...t, id: t.uuid }))}
                                    setList={(newState: any[]) => {
                                        const mapped: TabState[] = newState.map(item => ({
                                            uuid: item.id || item.uuid,
                                            sequence: item.sequence,
                                            label: item.label,
                                        }));
                                        setReorderTabs(mapped);
                                    }}
                                    animation={150}
                                    ghostClass="ghost"
                                    handle=".stage-drag-handle"
                                >
                                    {reorderTabs.map((tab, idx) => (
                                        <div key={tab.uuid} style={{ padding: 25, border: '1px solid #eee', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span className="stage-drag-handle" style={{ cursor: isStageMutationDisabled ? 'not-allowed' : 'grab', marginRight: 12, paddingTop: 5 }}><DragHandleIcon fontSize="large" /></span>
                                                <div>{tab.label}</div>
                                            </div>
                                        </div>
                                    ))}
                                </Sortable>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                                    <button type="button" onClick={closeReorderDialog} style={{ padding: '8px 12px', background: '#eee', borderRadius: 4, border: 'none' }} disabled={reorderSaving}>Cancel</button>
                                    <button type="button" onClick={handleReorderSave} style={{ padding: '8px 12px', background: '#1976d2', color: '#fff', borderRadius: 4, border: 'none' }} disabled={reorderSaving || isStageMutationDisabled}>{reorderSaving ? 'Saving...' : 'Save'}</button>
                                </div>
                            </div>
                        </Modal>
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            Note: Displayed SWL of bollards to be confirmed during MPX
                        </div>
                    </div>
                )
            }
            {/* Stage Name Dialog */}
            <Modal
                open={showStageDialog}
                onClose={closeStageDialog as any}
                heading="Create New Tug Stage"
            >
                <form onSubmit={handleStageDialogSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 320 }}>
                    <label style={{ fontWeight: 500, marginBottom: 4 }} htmlFor="stage-name-input">Stage Name</label>
                    <input
                        id="stage-name-input"
                        type="text"
                        value={stageName}
                        onChange={handleStageNameChange}
                        style={{
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            fontSize: 16,
                        }}
                        autoFocus
                        disabled={isStageMutationDisabled}
                    />
                    {stageError && <span style={{ color: 'red', fontSize: 14 }}>{stageError}</span>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <button
                            type="button"
                            onClick={closeStageDialog}
                            style={{
                                padding: '8px 16px',
                                background: '#eee',
                                color: '#333',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '8px 16px',
                                background: '#1976d2',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: isStageMutationDisabled ? 'not-allowed' : 'pointer',
                            }}
                            disabled={isStageMutationDisabled}
                        >
                            {loading ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={showDeleteModal}
                onClose={closeDeleteStageDialog as any}
                heading="Delete Stage?"
            >
                <div style={{ minWidth: 320 }}>
                    <div style={{ marginBottom: 16 }}>
                        {`Are you sure you want to delete the stage `}
                        <b>{selectedStage && selectedStage.label}</b>
                        {`?`}
                    </div>
                    {stageError && <span style={{ color: 'red', fontSize: 14 }}>{stageError}</span>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button
                            type="button"
                            onClick={closeDeleteStageDialog}
                            style={{
                                padding: '8px 16px',
                                background: '#eee',
                                color: '#333',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDeleteStage}
                            style={{
                                padding: '8px 16px',
                                background: '#d32f2f',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: isStageMutationDisabled ? 'not-allowed' : 'pointer',
                            }}
                            disabled={isStageMutationDisabled}
                        >
                            {loading ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Edit/Rename Modal */}
            <Modal
                open={showEditModal}
                onClose={closeEditStageDialog as any}
                heading="Rename Stage"
            >
                <form onSubmit={handleEditStage} style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 320 }}>
                    <label style={{ fontWeight: 500, marginBottom: 4 }} htmlFor="edit-stage-name-input">New Stage Name</label>
                    <input
                        id="edit-stage-name-input"
                        type="text"
                        value={editStageName}
                        onChange={e => setEditStageName(e.target.value)}
                        style={{
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            fontSize: 16,
                        }}
                        autoFocus
                        disabled={isStageMutationDisabled}
                    />
                    {stageError && <span style={{ color: 'red', fontSize: 14 }}>{stageError}</span>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <button
                            type="button"
                            onClick={closeEditStageDialog}
                            style={{
                                padding: '8px 16px',
                                background: '#eee',
                                color: '#333',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '8px 16px',
                                background: '#1976d2',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: isStageMutationDisabled ? 'not-allowed' : 'pointer',
                            }}
                            disabled={isStageMutationDisabled}
                        >
                            {loading ? 'Renaming...' : 'Rename'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default TugLinePositionsTabs;
